'use client';
/* global NodeJS */
import './productCard.scss';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import orderType from '@/constants/enums/orderType';
/* import enums & context */
import CardDetailPopover from '@/components/sider/Dialog/cardDetailPopover';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import customToaster from '@/components/toast/CustomToaster';
import { useShoppingCart } from '@/context/ShoppingCartContext';
import Tooltip from '@mui/material/Tooltip';
import {
  checkVantor,
  checkVantorBands,
  existingImgResText,
  mixPanelDateFormat,
  uiArchiveElementsValueMapper,
} from '@/lib/utils';

/* import mixpanel */
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEvent, MixpanelEventHub } from '@/services/eventEnums';
import rectangle from '../../../assets/img/rectangle.png';

import moment from 'moment';

import { Trans, useTranslation } from 'react-i18next';
import { ExistingImageCardData } from './cartInterfaces';
import { useSpinnerContext } from '@/context/SpinnerContext';
import { triggerEvent } from '@/lib/events';
import { emitMixpanelEvents } from '@/components/sider/sider.utils';
import { ProductCardType } from '@/components/sider/sider.types';
import ArchiveSourceComponent from './archiveSourceComponent';
import { PreviewLayer } from '@/context/MapContext/MapProvider';
import { useRouter } from 'next/navigation';
import OffPlatformRequestDialog from './offPlatformRequestDialog';
import { platformApps } from '@/constants/enums/enums';
import customDurationInfoToast from '@/components/toast/customDurationInfoToast';
import K from '@/constants/constants';
import { isPlatformHub } from '@/constants';

export default function ProductCard({
  cardData,
  handleOnMouseEnterDraw,

  handleOnMouseLeaveRemove,
  handleOnClickPreviewImage,
  handleOnClickRemovePreviewImage,
  previewRefMap,
  previewLayer,
  polygonCoordinates,
  multiProduct,
}: {
  cardData: ProductCardType;

  handleOnMouseEnterDraw: Function;
  handleOnMouseLeaveRemove: Function;
  handleOnClickPreviewImage: (
    coordinates: number[][][],
    intersectionCoordinates: number[][][] | null,
    bbox: number[],
    url: string,
    area: number,
    id: string
  ) => void;
  handleOnClickRemovePreviewImage: Function;
  previewRefMap: React.MutableRefObject<any>;

  previewLayer: PreviewLayer;
  polygonCoordinates: number[][][];
  multiProduct: boolean;
}) {
  const [imageUrl, setImageUrl] = useState<string>(cardData?.thumbnailImg);
  const {
    increaseCartQuantity,
    getItemQuantity,
    cartItems,
    AddPrimaryMergeItemCart,
    AddSecondaryMergeItemCart,
    mergeItemRef,
    mergeOverlapPrimaryItemRef,
    mergeOverlapRef,
    disableBandsRef,
    disableConstellationRef,
    disableSrcRef,
    removeSelectionItemCart,
    multiProductCartItemRef,
    archiveUiElementsRef,
  } = useShoppingCart(); // getting the context methods
  const { startLoaderEvent, stopLoaderEvent } = useSpinnerContext();
  const router = useRouter();
  const quantity = getItemQuantity(cardData?.id); // getting item quantity in the cart
  const cardRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<number>(1);
  const imageRef = useRef<HTMLImageElement>(null);
  const archiveIconRef = useRef<HTMLElement>(null);
  const isImageLoadedRef = useRef<boolean>(false);
  const [openOffPlatformDialog, setOpenOffPlatformDialog] = useState(false);
  const { t } = useTranslation('orderImagery');
  const isHub = isPlatformHub;

  const disabledSource = disableSrcRef.current;
  const disabledBands = disableBandsRef.current;
  const disabledConstellation = disableConstellationRef?.current;
  // const isNewArchiveDesignEnabled = posthog.isFeatureEnabled('27_June_2025_New_Archive_Design');
  const isNewArchiveDesignEnabled = true;
  useEffect(() => {
    let timeoutID: NodeJS.Timeout | null = null;

    function retryImageLoad() {
      timeoutID = setTimeout(() => {
        if (!isImageLoadedRef.current) {
          if (retryRef.current <= 10) {
            retryRef.current = retryRef.current + 1;
            const imageLoad = new Image();
            imageLoad.src = cardData.thumbnailImg;

            imageLoad.onload = e => {
              isImageLoadedRef.current = true;
              imageRef?.current && (imageRef.current.src = cardData.thumbnailImg);

              if (archiveIconRef.current) {
                archiveIconRef.current.hidden = true;
              }
            };
            retryImageLoad();
          }
        }
      }, 2000 * retryRef.current);
    }

    retryImageLoad();

    const cleanup = cardRef.current;
    // if preview exist but not highlighted then highlight it
    if (previewRefMap.current[cardData.id] && !cleanup?.classList.contains('active'))
      cleanup?.classList.add('active');

    return () => {
      // this is to clear previous pending timeout when there is new search initiated
      if (timeoutID) clearTimeout(timeoutID);
      if (cleanup) cleanup.classList.remove('active');
    };
  }, [cardData?.thumbnailImg, cardData?.id, previewRefMap]);

  const addToCarthandler = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: string,
    type: string,
    cardData: ExistingImageCardData | (ProductCardType & { drawPolygonCoordinates?: number[][][] }),
    isMultiProduct: boolean = false
  ) => {
    let standardOrder = true;
    if (archiveUiElementsRef.current?.data) {
      const elements: any[] = archiveUiElementsRef.current?.data?.elements;

      const isAreaValid = elements?.every((element: any) => {
        if (element?.sources?.includes(cardData?.source)) {
          const standardMinAoi = element?.rules?.standard_order?.aoi_limit?.min;
          const standardMaxAoi = element?.rules?.standard_order?.aoi_limit?.max;
          const nonStandardMax = element?.rules?.non_standard_order?.aoi_limit?.max;
          const selectedProductIntersectedArea = cardData?.area;

          if (standardMinAoi && selectedProductIntersectedArea < parseInt(standardMinAoi)) {
            customToaster(
              t('selectAreaGreaterCustom', { area: standardMinAoi, source: cardData?.source })
            );
            return false;
          }

          if (isHub && isMultiProduct) {
            if (standardMaxAoi && selectedProductIntersectedArea > parseInt(standardMaxAoi)) {
              customToaster(
                t('selectAreaLesserCustom', { area: standardMaxAoi, source: cardData?.source })
              );
              return false;
            }
          }

          if (
            standardMaxAoi &&
            selectedProductIntersectedArea > parseInt(standardMaxAoi) &&
            (nonStandardMax ? selectedProductIntersectedArea < parseInt(nonStandardMax) : true)
          ) {
            standardOrder = false;
          }

          if (nonStandardMax && selectedProductIntersectedArea > parseInt(nonStandardMax)) {
            setOpenOffPlatformDialog(true);

            MixpanelTracking.getInstance().track(
              MixpanelEvent.LARGE_AREA_ORDER_ARHIVE_ADD_TO_CART_CLICK,
              {
                date_taken: mixPanelDateFormat(cardData?.startDate),
                source: cardData?.source,
                resolution: cardData?.resolution,
                price: cardData?.price,
                search_id: cardData?.searchId,
                result_id: cardData?.id,
                offering_id: cardData?.originalData?.product?.configuration?.offering_id,
                data_type: cardData?.originalData?.sensor_type,
                area: cardData?.area,
              }
            );

            MixpanelTracking.getInstance().track(
              MixpanelEventHub.ORDER_NEW_LARGE_AREA_ORDER_DIALOG_OPEN,
              {
                data_type: cardData?.originalData?.sensor_type,
              }
            );

            return false;
          }
        }

        return true;
      });

      if (!isAreaValid) {
        return false;
      }
    }

    let updatedCardData = { ...cardData, standardOrder };
    // if (
    //   cardData?.source === 'PlanetScope-SuperDove' ||
    //   cardData?.source === 'Planetscope' ||
    //   cardData?.source === 'Spot'
    // ) {
    //   if (cardData?.originalSearchedArea < 5) {
    //     customToaster(t('mediumResMinOrderSizePara'));
    //     return false;
    //   }
    // }
    // if (cardData?.source === 'SkySat') {
    //   if (cardData?.originalSearchedArea < 25 || cardData?.area < 25) {
    //     customToaster(t('areaForSkysatPara'));
    //     return false;
    //   }
    // }

    // if (cardData.archiveOfferingId && offeringIds[cardData.archiveOfferingId]) {
    //   if (cardData.originalSearchedArea < offeringIds[cardData.archiveOfferingId].min) {
    //     customToaster(t('selectAreaGreater'));
    //     return false;
    //   }

    //   if (
    //     offeringIds[cardData.archiveOfferingId].max !== 0 &&
    //     cardData.originalSearchedArea > offeringIds[cardData.archiveOfferingId].max
    //   ) {
    //     customToaster(
    //       t('selectAreaLesserCustom', { area: offeringIds[cardData.archiveOfferingId].max })
    //     );
    //     return false;
    //   }
    // }

    //setting the draw polygon coordinates
    cardData.drawPolygonCoordinates = polygonCoordinates;
    // if multi product is active
    if (isHub && isMultiProduct) {
      //check for the primary item if not then create the primary item.
      if (!mergeOverlapPrimaryItemRef.current) {
        mergeOverlapPrimaryItemRef.current = true;

        AddPrimaryMergeItemCart(id, type, updatedCardData);
      } else {
        //Add the secondary item
        //Adding the loader for the price calculation API
        triggerEvent(startLoaderEvent);
        await AddSecondaryMergeItemCart(id, updatedCardData, polygonCoordinates);
        triggerEvent(stopLoaderEvent);
      }
    } else {
      const removePreviewCallback = () => {
        //remove the highlight and preview for the current card
        if (cardRef.current && cardRef.current.classList.contains('active')) {
          handleOnClickRemovePreviewImage(cardData.id);
          cardRef.current?.classList.remove('active');
        }
      };
      increaseCartQuantity(id, type, updatedCardData, removePreviewCallback);
      emitMixpanelEvents(MixpanelEventHub.HUB_EXPLORE_EXISTING_ADD_TO_CART_CLICK, {
        date_taken: mixPanelDateFormat(cardData?.startDate),
        source: cardData?.source,
        resolution: cardData?.resolution,
        price: cardData?.price,
        search_id: cardData?.searchId,
        result_id: cardData?.id,
        offering_id: cardData?.originalData?.product?.configuration?.offering_id,
        data_type: cardData?.originalData?.sensor_type,
        order_type: standardOrder ? 'standard' : 'non standard',
      });
    }

    const nonStandardOrderMessage =
      process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.EXPLORE ? (
        <Trans
          i18nKey="orderImagery:nonStandardOrderNoteExploreWithoutNote"
          components={{
            anchor: (
              <Link
                color="inherit"
                href={process.env.NEXT_PUBLIC_SKYWATCH_STANDARD_FORMAT_INTERCOM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
              />
            ),
          }}
        />
      ) : (
        <Trans
          i18nKey="orderImagery:nonStandardOrderNoteHUBWithoutNote"
          components={{
            anchor: (
              <Link
                color="inherit"
                href={process.env.NEXT_PUBLIC_SKYWATCH_STANDARD_FORMAT_INTERCOM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
              />
            ),
          }}
        />
      );
    !isMultiProduct &&
      updatedCardData?.standardOrder === false &&
      customDurationInfoToast(nonStandardOrderMessage, 'info-toast', 10000, 'info');

    !isMultiProduct && customToaster(t('addedToCart'), 'success');

    e.preventDefault();
    e.stopPropagation();
    return true;
  };
  const resolution = cardData.resolution;
  const resolutionText = existingImgResText(resolution, true);
  const isVantorProduct = checkVantor(cardData);
  const vantorBands = checkVantorBands(cardData);

  const checkDisability = useMemo(() => {
    if (disabledSource) {
      //if the disabled item is vantor
      if (disabledSource?.includes(K.LABELS.SOURCES.VANTOR)) {
        if (
          disabledSource === cardData?.source &&
          disabledBands === cardData?.bandsLength &&
          disabledConstellation === cardData?.constellation
        ) {
          return false;
        } else {
          return true;
        }
      }
      if (disabledSource === cardData?.source) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }, [cardData, disabledSource, disabledBands, disabledConstellation]);

  const checkMonthDisability = useMemo(() => {
    //time changes

    let monthDifference = 0;

    //if the multi product exist then calculate the month difference of the same source
    if (
      multiProduct &&
      !checkDisability &&
      multiProductCartItemRef.current?.overlapData &&
      multiProductCartItemRef.current?.overlapData.length > 0
    ) {
      const primaryCardData = multiProductCartItemRef.current?.overlapData[0];
      const startDatePrimaryPolygon = moment(primaryCardData.startDate);
      const endDateSecondaryPolygon = moment(cardData.startDate);

      monthDifference = startDatePrimaryPolygon.diff(endDateSecondaryPolygon, 'months');

      if (monthDifference < 0) monthDifference *= -1;
    }

    return monthDifference;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDisability, multiProduct, cardData, multiProductCartItemRef.current?.overlapData]);

  const isProductLocked =
    !quantity &&
    !mergeItemRef.current[cardData.id] &&
    (checkDisability || checkMonthDisability >= 3);

  const isProductAdded = quantity !== 0 || mergeItemRef.current[cardData.id];

  let elementWithValueData: any = [];

  archiveUiElementsRef.current?.data?.elements?.forEach((item: any) => {
    if (item?.sources?.includes(cardData?.source)) {
      elementWithValueData = uiArchiveElementsValueMapper(
        cardData?.originalData,
        item?.products,
        t
      );
    }
  });

  const iconMapper: any = {
    preview_image_icon: <VisibilityOutlinedIcon />,
    info_icon: <InfoOutlinedIcon />,
    redirect_icon: <LaunchOutlinedIcon />,
  };
  const getUiIcon = (icon: string) => {
    return iconMapper[icon] ?? null;
  };
  const handle_preview_image = async (e: any) => {
    if (
      !cardRef.current ||
      checkDisability ||
      (multiProduct && (quantity > 0 || mergeItemRef.current[cardData.id])) ||
      checkMonthDisability >= 3
    )
      return;
    if (previewRefMap.current[cardData.id]) {
      handleOnClickRemovePreviewImage(cardData.id);
      cardRef.current?.classList.remove('active');
      multiProduct && removeSelectionItemCart(cardData.id);
      // previewRef.current = '';
    } else {
      let error = true;
      multiProduct &&
        (error = await addToCarthandler(e, cardData.id, orderType.EXISTING, cardData, true));

      MixpanelTracking.getInstance().track(MixpanelEvent.EXPLORE_EXISTING_RESULT_PREVIEW_CLICK, {
        result_id: cardData.id,
        date_taken: cardData.startDate,
        source: cardData.source,
        resolution: cardData.resolution,
        price: cardData.price,
        est_cloud_coverage: cardData.cloudCoverage,
        search_id: cardData.searchId,
      });

      if (!error) return;
      // previewRef.current = cardData.previewImg;

      handleOnClickPreviewImage(
        cardData.coordinates,
        cardData.intersectionCoordinates,
        cardData.bbox,
        cardData.previewImg,
        cardData.area > cardData.minimumArea ? cardData.area : cardData.minimumArea,
        cardData.id
      );

      cardRef.current?.classList.add('active');
    }
  };

  const onClickMapper: any = {
    handle_preview_image: async (e: any) => {
      e?.stopPropagation();
      await handle_preview_image(e);
    },
  };

  return (
    <Card
      ref={cardRef}
      aria-disabled={'true'}
      aria-label="product-card-element"
      className={`product-card existing-product-card ${!checkDisability && !(multiProduct && (quantity > 0 || mergeItemRef.current[cardData.id])) && !(checkMonthDisability >= 3) && 'card-active'}`}
      onMouseEnter={e => {
        if (!checkDisability) handleOnMouseEnterDraw(cardData.coordinates);
      }}
      onMouseLeave={e => {
        handleOnMouseLeaveRemove();
      }}
      onClick={async (e: any) => {
        e?.stopPropagation();
        await handle_preview_image(e);
      }}
    >
      {openOffPlatformDialog && (
        <OffPlatformRequestDialog
          type="archive"
          archiveData={cardData}
          open={openOffPlatformDialog}
          setOpen={setOpenOffPlatformDialog}
        />
      )}

      <Tooltip
        aria-label="card-tooltip"
        title={
          quantity > 0 || mergeItemRef.current[cardData.id]
            ? t('imageAlreadyExist')
            : checkMonthDisability >= 3
              ? t('threeMonthImage')
              : t('sameSourceImage')
        }
        disableHoverListener={
          checkDisability ||
          checkMonthDisability >= 3 ||
          quantity > 0 ||
          mergeItemRef.current[cardData.id]
            ? false
            : true
        }
        placement="right-start"
        classes={{ tooltip: 'lock-tooltip-wrap' }}
        slotProps={{
          tooltip: {
            sx: {
              color: '#fff',
              backgroundColor: '#496178',
              padding: '8px',
            },
          },
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [40, -200],
                },
              },
            ],
          },
        }}
      >
        <Box className="card-content-wrapper">
          <Box
            className={`img-wrapper ${isProductAdded || isProductLocked ? 'added-img-thumbnail' : ''}`}
          >
            <CardMedia
              component="img"
              ref={imageRef}
              src={isImageLoadedRef.current ? cardData?.thumbnailImg : rectangle.src}
              key={`${imageUrl}-${retryRef.current}`}
              alt="Cart Item Picture"
            />
            {/* lock icon */}
            <i ref={archiveIconRef} aria-label="label-icon-archive" className="icon-archive"></i>

            {isProductLocked && <i aria-label="label-icon-lock" className="icon-lock"></i>}

            {isProductAdded && (
              <i aria-label="label-icon-card-added" className="icon-cart-added"></i>
            )}
          </Box>
          <CardContent aria-disabled={true}>
            <Box className="card-info-wrapper">
              <Box className="card-info">
                {elementWithValueData?.cardElements?.column1Elements?.map(
                  (item: any, index: any) => (
                    <React.Fragment key={index}>
                      {item?.titleElements?.map((titleElement: any, index: any) => (
                        <Typography key={index} variant="h6" className="product-date">
                          {titleElement?.value}
                        </Typography>
                      ))}
                      <Stack
                        aria-label="card-data-src-ui"
                        key={index}
                        direction="column"
                        spacing={0.5}
                      >
                        {item?.contentElements?.map((contentElements: any, index: any) =>
                          contentElements?.name === 'source' && !isNewArchiveDesignEnabled ? (
                            <ArchiveSourceComponent
                              key={index}
                              cardData={cardData}
                              handleOnMouseLeaveRemove={handleOnMouseLeaveRemove}
                              value={contentElements?.value}
                              popupElements={elementWithValueData?.popUpElements}
                            />
                          ) : contentElements?.value ? (
                            <Typography variant="caption" display="block" key={index}>
                              {contentElements?.value}
                            </Typography>
                          ) : null
                        )}
                      </Stack>
                    </React.Fragment>
                  )
                )}
              </Box>
              <Box className="card-info align-items-end text-right">
                {elementWithValueData?.cardElements?.column2Elements?.map(
                  (item: any, index: any) => (
                    <React.Fragment key={index}>
                      {item?.titleElements?.map((titleElement: any, index: any) => (
                        <Typography key={index} variant="h6" className="product-date">
                          {titleElement?.value}
                        </Typography>
                      ))}
                      {item?.contentElements?.map((contentElements: any, index: any) => {
                        if (contentElements?.name === 'actions') {
                          return (
                            <Box key={index} className="product-card-actions">
                              {contentElements?.options?.map((childItem: any, index: any) => {
                                if (childItem?.name === 'info_popup') {
                                  return (
                                    <CardDetailPopover
                                      key={index}
                                      handleOnMouseLeaveRemove={handleOnMouseLeaveRemove}
                                      cardData={cardData}
                                      popupElements={elementWithValueData?.popUpElements}
                                      className="icon-buttons"
                                    />
                                  );
                                } else {
                                  return (
                                    <IconButton
                                      className="icon-buttons"
                                      color="secondary"
                                      key={index}
                                      {...(childItem?.name === 'redirect' && {
                                        component: 'a',
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                        href: `${cardData?.productId && cardData?.archiveOfferingId ? `/product-library/${cardData?.productId}?offeringId=${cardData?.archiveOfferingId}&category=archive` : '/product-library'}`,
                                      })}
                                      {...(childItem?.name !== 'redirect' && {
                                        onClick: (e: any) => {
                                          const onClicker = onClickMapper[`${childItem?.on_click}`];
                                          if (onClicker) {
                                            onClicker(e);
                                          }
                                        },
                                      })}
                                    >
                                      {getUiIcon(childItem?.icon)}
                                    </IconButton>
                                  );
                                }
                              })}
                            </Box>
                          );
                        }

                        if (contentElements?.name !== 'actions') {
                          return (
                            <Typography key={index} variant="caption" display="block">
                              {contentElements?.value}
                            </Typography>
                          );
                        }

                        // if(contentElements?.name === 'actions'){

                        //     <Box key={index}>
                        //       {
                        //         contentElements?.options?.map((childItem:any,index:any)=>{
                        //           return (
                        //             <IconButton key={index} aria-label="delete">
                        //               {iconMapper[childItem?.icon]}
                        //             </IconButton>
                        //           )
                        //         })
                        //       }
                        //     </Box>

                        // }else {
                        //   return (
                        //     <Typography key={index} variant="caption" display="block">
                        //     {contentElements?.value}
                        //   </Typography>
                        //   )
                        // }
                      })}
                    </React.Fragment>
                  )
                )}
                {quantity === 0 && !mergeItemRef.current[cardData.id] ? (
                  <Stack direction="column" spacing={0.5}>
                    {!multiProduct && (
                      <Chip
                        onClick={e => {
                          //check if the card is already highlighted or not
                          if (cardRef.current && !cardRef.current.classList.contains('active')) {
                            handleOnClickPreviewImage(
                              cardData.coordinates,
                              cardData.intersectionCoordinates,
                              cardData.bbox,
                              cardData.previewImg,
                              cardData.area > cardData.minimumArea
                                ? cardData.area
                                : cardData.minimumArea,
                              cardData?.id
                            );

                            cardRef.current?.classList.add('active');
                          }
                          let updatedCardData = cardData;
                          const filterElementsData =
                            archiveUiElementsRef.current?.data?.elements?.filter((item: any) => {
                              return item?.sources?.includes(cardData?.source);
                            });
                          const cartDrawerElements = filterElementsData?.[0]?.cart_drawer;
                          const cartElements = filterElementsData?.[0]?.cart;
                          updatedCardData = { ...cardData, cartDrawerElements, cartElements };

                          addToCarthandler(e, cardData.id, orderType.EXISTING, updatedCardData);
                        }}
                        size="small"
                        className="add-area-btn"
                        label={
                          <>
                            <AddShoppingCartOutlinedIcon />
                            {t('add')}
                          </>
                        }
                        variant="outlined"
                        disabled={checkDisability}
                        aria-label="add-to-cart-btn"
                      />
                    )}
                  </Stack>
                ) : (
                  <Stack direction="column" spacing={0.5}>
                    {!multiProduct && (
                      <Chip
                        disabled={true}
                        aria-disabled={true}
                        size="small"
                        className="add-area-btn"
                        label={
                          <>
                            <i className="icon-cart-added"></i>
                            {t('added')}
                          </>
                        }
                        aria-label="added-to-cart-btn"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                )}
              </Box>
            </Box>
          </CardContent>
        </Box>
      </Tooltip>
    </Card>
  );
}
